import type { QueryKey } from '@tanstack/react-query';
import { getGetKeyQueryKey, useGetKey, useMetaData } from '@uturn/api/v1';
import { MeiliSearch } from 'meilisearch';
import { createContext, useContext, useMemo } from 'react';

export const MeiliSearchContext = createContext<MeiliSearch | null>(null);

export const useMeiliSearch = () => {
	const meiliSearch = useContext(MeiliSearchContext);
	return meiliSearch;
};

export const useMeiliSearchKey = (): QueryKey => {
	const { data: metaData } = useMetaData();
	const meiliSearchKey = getGetKeyQueryKey();
	const userId = metaData?.data.user?.id;
	if (!userId) {
		return meiliSearchKey;
	}
	return [...meiliSearchKey, userId];
};

export const meiliSearchIndex = (meiliSearch: MeiliSearch) => {
	const shipmentsIndex = meiliSearch.index(
		import.meta.env.VITE_MEILISEARCH_INDEX,
	);
	return shipmentsIndex;
};

export const MeiliSearchProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const queryKey = useMeiliSearchKey();
	const { data: metaData } = useMetaData();
	const userId = metaData?.data.user?.id;

	const { data: apiKey, isSuccess } = useGetKey({
		query: {
			staleTime: 1000 * 60 * 10, // 10 minutes
			refetchInterval: 1000 * 60 * 29, // 29 minutes
			refetchIntervalInBackground: true,
			refetchOnWindowFocus: true,
			queryKey,
		},
	});

	const meiliSearch = useMemo(() => {
		if (isSuccess && apiKey?.data) {
			return new MeiliSearch({
				host: import.meta.env.VITE_MEILISEARCH_HOST,
				apiKey: apiKey.data,
				// Identifies the user in Meilisearch search analytics. Without it
				// Meilisearch generates a fresh id per request, so every search
				// looks like a distinct user.
				requestConfig: userId
					? { headers: { 'X-MS-USER-ID': String(userId) } }
					: undefined,
			});
		}
		return null;
	}, [apiKey?.data, isSuccess, userId]);

	return (
		<MeiliSearchContext.Provider value={meiliSearch}>
			{children}
		</MeiliSearchContext.Provider>
	);
};
